import type {
  FloorSheetDeposition,
  FloorSheetFsTimes,
  FloorSheetMisfireRecord,
  FloorSheetParamRecord
} from './floorSheetCrcFields'

export const BEVEL_COPY_SECTIONS = ['Bevel measurement', 'Pup 1', 'Pup 2'] as const
export const ROOT_BEAD_COPY_SECTIONS = ['All IWM heads'] as const
export const ROOT_MISFIRE_COPY_SECTIONS = ['Meta fields', 'CW cells', 'CCW cells'] as const
export const WELD_COMPLETION_COPY_TAB = 'Weld Completion Times'
export const OPERATOR_COPY_TAB = 'Operator Information'

export type BevelMeasurementsByPup = {
  pupOne: Record<string, Record<string, string>>
  pupTwo: Record<string, Record<string, string>>
}

export type WeldLimitSettings = Record<
  string,
  { min: string; max: string; unit: string }
>

export type PreheatInterpassBounds = {
  min: string
  max: string
  avg?: string
}

export type FloorSheetCopyOperators = {
  equipment: string[]
  operatorMap: Record<string, { name: string; id: string }>
  rows: Array<Record<string, unknown>>
  notes: string
}

export type FloorSheetCopyWeld = {
  pupOne: string
  pupTwo: string
  pupOneSide: string
  pupTwoSide: string
  pupOneSeamLocation: string
  pupTwoSeamLocation: string
  bevelUnit: 'in' | 'mm'
  bevelMeasurementsByPup: BevelMeasurementsByPup
  limitSettings: WeldLimitSettings
  preheatInterpassUnit?: '°F' | '°C'
  preheatInterpassMeasurementsByPass?: Record<string, Record<string, string>>
  preheatInterpassByPass?: Record<string, PreheatInterpassBounds>
  deposition: FloorSheetDeposition
  paramRecord: FloorSheetParamRecord
  fsTimes: FloorSheetFsTimes
  operators: FloorSheetCopyOperators
}

export function copySectionsForTab(
  tab: string,
  passLabels: string[]
): string[] {
  let tabSections: string[] = []
  switch (tab) {
    case 'Bevel Measurements':
      tabSections = [...BEVEL_COPY_SECTIONS]
      break
    case 'Preheat / Interpass':
      tabSections = [...passLabels]
      break
    case 'Deposition':
      tabSections = [...passLabels]
      break
    case 'Root Bead Record':
      tabSections = [...ROOT_BEAD_COPY_SECTIONS]
      break
    case 'Root Misfire Record':
      tabSections = [...ROOT_MISFIRE_COPY_SECTIONS]
      break
    case WELD_COMPLETION_COPY_TAB:
      tabSections = [WELD_COMPLETION_COPY_TAB]
      break
    case OPERATOR_COPY_TAB:
      tabSections = [OPERATOR_COPY_TAB]
      break
    default:
      tabSections = []
  }
  return tabSections
}

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function copyMisfireMeta(source: FloorSheetMisfireRecord, target: FloorSheetMisfireRecord) {
  return {
    ...target,
    powerSupply: source.powerSupply,
    polarity: source.polarity,
    process: source.process,
    progression: source.progression,
    wireFeedSpeed: source.wireFeedSpeed,
    completedBy: source.completedBy,
    completedByMemberId: source.completedByMemberId
  }
}

function copyMisfireSide(
  source: FloorSheetMisfireRecord,
  target: FloorSheetMisfireRecord,
  side: 'CW' | 'CCW'
) {
  return {
    ...target,
    cells: {
      ...target.cells,
      [side]: cloneValue(source.cells[side])
    }
  }
}

export function applyFloorSheetCopy<T extends FloorSheetCopyWeld>(
  source: T,
  target: T,
  tab: string,
  selectedSections: Set<string>,
  sourcePassLabels: string[]
): T {
  const next: T = { ...target }

  if (selectedSections.has(WELD_COMPLETION_COPY_TAB)) {
    next.fsTimes = cloneValue(source.fsTimes)
  }
  if (selectedSections.has(OPERATOR_COPY_TAB)) {
    next.operators = cloneValue(source.operators)
  }

  if (tab === 'Bevel Measurements') {
    if (selectedSections.has('Bevel measurement')) {
      next.bevelMeasurementsByPup = cloneValue(source.bevelMeasurementsByPup)
      next.limitSettings = cloneValue(source.limitSettings)
      next.bevelUnit = source.bevelUnit
    }
    if (selectedSections.has('Pup 1')) {
      next.pupOne = source.pupOne
      next.pupOneSide = source.pupOneSide
      next.pupOneSeamLocation = source.pupOneSeamLocation
    }
    if (selectedSections.has('Pup 2')) {
      next.pupTwo = source.pupTwo
      next.pupTwoSide = source.pupTwoSide
      next.pupTwoSeamLocation = source.pupTwoSeamLocation
    }
    return next
  }

  if (tab === 'Preheat / Interpass') {
    const measurements = { ...(next.preheatInterpassMeasurementsByPass ?? {}) }
    for (const pass of sourcePassLabels) {
      if (!selectedSections.has(pass)) continue
      if (source.preheatInterpassMeasurementsByPass?.[pass]) {
        measurements[pass] = cloneValue(source.preheatInterpassMeasurementsByPass[pass])
      }
    }
    next.preheatInterpassMeasurementsByPass = measurements
    next.preheatInterpassByPass = Object.entries(measurements).reduce(
      (acc, [pass, byLocation]) => {
        const values = Object.values(byLocation)
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n))
        if (!values.length) {
          acc[pass] = { min: '', max: '', avg: '' }
          return acc
        }
        const min = Math.min(...values)
        const max = Math.max(...values)
        const avg = values.reduce((sum, n) => sum + n, 0) / values.length
        acc[pass] = {
          min: String(min),
          max: String(max),
          avg: String(Number(avg.toFixed(2)))
        }
        return acc
      },
      {} as Record<string, PreheatInterpassBounds>
    )
    return next
  }

  if (tab === 'Deposition') {
    const deposition = { ...next.deposition, byPass: { ...next.deposition.byPass } }
    for (const pass of sourcePassLabels) {
      if (!selectedSections.has(pass)) continue
      if (source.deposition.byPass[pass]) {
        deposition.byPass[pass] = cloneValue(source.deposition.byPass[pass])
      }
    }
    next.deposition = deposition
    return next
  }

  if (tab === 'Root Bead Record') {
    if (selectedSections.has('All IWM heads')) {
      next.paramRecord = {
        ...next.paramRecord,
        iwmHeads: cloneValue(source.paramRecord.iwmHeads)
      }
    }
    return next
  }

  if (tab === 'Root Misfire Record') {
    let misfire = { ...next.paramRecord.misfire }
    if (selectedSections.has('Meta fields')) {
      misfire = copyMisfireMeta(source.paramRecord.misfire, misfire)
    }
    if (selectedSections.has('CW cells')) {
      misfire = copyMisfireSide(source.paramRecord.misfire, misfire, 'CW')
    }
    if (selectedSections.has('CCW cells')) {
      misfire = copyMisfireSide(source.paramRecord.misfire, misfire, 'CCW')
    }
    next.paramRecord = { ...next.paramRecord, misfire }
    return next
  }

  return next
}

export type FloorSheetCopyPanelWeld = FloorSheetCopyWeld & {
  id: string
  weldNumber?: string
  sheetName?: string
  weldId?: string
}

export function weldCopyLabel(weld: Pick<FloorSheetCopyPanelWeld, 'weldNumber' | 'sheetName' | 'weldId'>, index: number): string {
  const number = weld.weldNumber?.trim() || weld.sheetName?.trim() || weld.weldId?.trim()
  return number ? `Weld ${number}` : `Weld ${index + 1}`
}
